import { useEffect, useRef } from "react";

// formik components
import { Formik, Form } from "formik";

// toast notifications
import { toast, Id } from "react-toastify";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// Page components
import PeriodForm from "./periodForm";
import CardSkeleton from "examples/Cards/CardSkeleton";
import CardError from "examples/Cards/CardError";

// API queries
import { useCountryLocCodes } from "api/features/country/queries";

// Contexts
import { useFormPrevProjContext } from "contexts/contextFormPrevProj";

import validations from "../../schemas/validations";
import getForm from "../../schemas/form";

function PrevProjForm(): JSX.Element {
  const {
    countryDefaults,
    appliedOptions,
    setAppliedOptions,
    isLoading,
    isFormLoading,
    isError,
    errorMessage,
    refetch,
  } = useFormPrevProjContext();

  const { data: countryLocCodes } = useCountryLocCodes();
  const { formId, formField } = getForm(countryLocCodes);
  const pendingSubmitRef = useRef<{ toastId: Id; values: any } | null>(null);

  const handleSubmit = (values: any, actions: any) => {
    if (pendingSubmitRef.current) {
      actions.setSubmitting(false);
      return;
    }

    pendingSubmitRef.current = {
      toastId: toast.loading("Loading projection data..."),
      values,
    };
    setAppliedOptions(values);
    actions.setSubmitting(false);
  };

  useEffect(() => {
    const pending = pendingSubmitRef.current;
    if (!pending || isLoading) return;
    if (appliedOptions !== pending.values) return;

    toast.update(pending.toastId, {
      render: isError
        ? errorMessage || "Failed to load projection data."
        : "Projection data submitted successfully!",
      type: isError ? "error" : "success",
      isLoading: false,
      autoClose: 2500,
    });
    pendingSubmitRef.current = null;
  }, [appliedOptions, isLoading, isError, errorMessage]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={countryDefaults}
      validationSchema={validations}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, isSubmitting }) => {
        const formData = { values, errors, touched, formField };
        return (
          <Form id={formId} autoComplete="off">
            {isFormLoading ? (
              <CardSkeleton height="12rem" />
            ) : isError ? (
              <CardError height="12rem" message={errorMessage} onRetry={refetch} />
            ) : (
              <MDBox>
                <MDBox mb={2.5}>
                  <MDButton
                    disabled={isSubmitting || Object.keys(errors).length > 0}
                    type="submit"
                    variant="gradient"
                    color="dark"
                  >
                    Submit
                  </MDButton>
                </MDBox>
                <PeriodForm formData={formData} />
              </MDBox>
            )}
          </Form>
        );
      }}
    </Formik>
  );
}

export default PrevProjForm;
